import { exactId, ScopedAccessApi, ScopedAssignment, ScopedCatalog, ScopedCompany, ScopedGrant, ScopedMutation, ScopedPage } from './scoped-access.models';

function settle<T>(promise: Promise<T>): Promise<{status: 'fulfilled'; value: T} | {status: 'rejected'; reason: unknown}> {
  return promise.then(value => ({status: 'fulfilled' as const, value}), reason => ({status: 'rejected' as const, reason}));
}

interface Attempt {
  kind: 'grant' | 'revoke'; target: string; key: string;
  body: ScopedGrant | {expectedCatalogVersion: string}; uncertain: boolean;
}
export class ScopedAccessState {
  readonly warning = 'Recorded only—these assignments do not currently grant access.';
  catalog: ScopedCatalog | null = null;
  page: ScopedPage | null = null;
  companies: ScopedCompany[] = [];
  companyId = '';
  includeHistory = false;
  loading = false;
  saving = false;
  denied = false;
  invalidated = false;
  listError = '';
  companyError = '';
  catalogError = '';
  mutationError = '';
  success = '';
  needsReload = false;
  private attempt: Attempt | null = null;
  private alive = true;
  private loadVersion = 0;

  constructor(readonly subjectId: string, private api: ScopedAccessApi,
              private getCompanies: (current: () => boolean) => Promise<ScopedCompany[]>, private eligible: () => boolean,
              private makeKey: () => string = () => crypto.randomUUID()) {}

  get bundle(): ScopedCatalog['bundles'][number] | undefined {
    return this.catalog?.bundles.find(b => b.bundleId === 'payroll_operations' && b.routineGrantable === true
      && b.breakGlass === false && b.scopeTypes.includes('company'));
  }
  get pending(): boolean { return this.attempt !== null; }
  get uncertain(): boolean { return !!this.attempt?.uncertain; }
  get current(): boolean { return this.alive && !this.denied && !this.invalidated && this.eligible(); }
  get canMutate(): boolean {
    return this.current && !this.loading && !this.saving && !this.pending && !this.needsReload
      && !!this.page && !this.listError && !this.catalogError && this.catalog?.enforcementStatus === 'dormant'
      && this.catalog.catalogVersion === this.page.catalogVersion && !!this.bundle;
  }
  get canGrant(): boolean { return this.canMutate && this.companies.some(c => c.id === this.companyId && c.type !== 'team'); }
  get canRetry(): boolean { return this.current && this.uncertain && !this.saving && !this.loading; }
  checkContext(): void {
    if (this.alive && !this.eligible()) {
      this.invalidated = true; this.destroy();
    }
  }
  destroy(): void {
    this.alive = false; ++this.loadVersion; this.page = null; this.companies = []; this.catalog = null;
    this.attempt = null; this.loading = false; this.saving = false;
  }
  private valid = (): boolean => { this.checkContext(); return this.current; };

  async initialize(): Promise<void> {
    if (!this.valid()) { return; }
    await Promise.all([this.loadCompanies(), this.load()]);
  }
  private async loadCompanies(): Promise<void> {
    try {
      const companies = await this.getCompanies(this.valid);
      if (!this.valid()) { return; }
      if (!Array.isArray(companies)) { throw new Error('Invalid company list'); }
      this.companies = companies.filter(c => exactId(c.id) && c.deletedAt == null && c.type !== 'team')
        .map(c => ({id: c.id, name: c.name, type: c.type})).sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id));
    } catch (_) {
      if (this.valid()) { this.companyError = 'Companies unavailable. History remains available by exact company ID. Close and reopen to retry.'; }
    }
  }
  async load(cursor: string | null = null): Promise<void> {
    if (!this.valid() || this.saving) { return; }
    const version = ++this.loadVersion;
    const history = this.includeHistory;
    this.loading = true; this.listError = ''; this.catalogError = ''; this.catalog = null;
    const current = () => this.valid() && version === this.loadVersion;
    try {
      // Each explicit refresh observes both contracts. A page version alone proves no grantability.
      const [catalogResult, pageResult] = await Promise.all([
        settle(this.api.catalog(current)), settle(this.api.list(this.subjectId, history, cursor, current)),
      ]);
      if (!current()) { return; }
      for (const result of [catalogResult, pageResult]) {
        if (result.status === 'rejected') { this.deny(result.reason); }
      }
      if (!current()) { return; }
      if (catalogResult.status === 'fulfilled' && catalogResult.value?.enforcementStatus === 'dormant'
        && exactId(catalogResult.value.catalogVersion) && Array.isArray(catalogResult.value.bundles)) {
        this.catalog = catalogResult.value;
        if (!this.bundle) { this.catalogError = 'Payroll operations is not manageable with the current catalog.'; }
      } else {
        this.catalogError = 'Management catalog unavailable. Refresh to try again.';
      }
      const page = pageResult.status === 'fulfilled' ? pageResult.value : null;
      if (!page || page.enforcementStatus !== 'dormant' || typeof page.revision !== 'string' || !/^\d+$/.test(page.revision)
        || !exactId(page.catalogVersion) || !Array.isArray(page.assignments) || page.assignments.length > 25
        || page.assignments.some(a => a.subject.type !== 'human_user' || a.subject.id !== this.subjectId)) {
        this.listError = 'Current assignment state is unavailable. Refresh to try again.';
      } else {
        this.page = page;
        if (this.catalog && this.catalog.catalogVersion !== page.catalogVersion) {
          this.catalog = null;
          this.catalogError = 'Catalog and assignment versions differ. Refresh and review before starting a new attempt.';
        }
      }
      this.needsReload = !!this.listError || !!this.catalogError;
    } catch (error) {
      if (current()) {
        this.catalog = null; this.needsReload = true;
        this.listError = 'Current assignment state is unavailable. Refresh to try again.';
        this.deny(error);
      }
    } finally { if (version === this.loadVersion) { this.loading = false; } }
  }
  async history(value: boolean): Promise<void> {
    if (this.saving || this.pending) { return; }
    this.includeHistory = value; this.page = null; await this.load();
  }
  companyLabel(id: string): string { return this.companies.find(c => c.id === id)?.name || 'Company label unavailable'; }
  bundleLabel(id: string): string { return this.catalog?.bundles.find(b => b.bundleId === id)?.label || id; }
  canRevoke(row: ScopedAssignment): boolean {
    return this.canMutate && !!this.page?.assignments.includes(row) && row.subject.id === this.subjectId
      && row.subject.type === 'human_user' && row.bundleId === 'payroll_operations' && row.scope.type === 'company'
      && ['effective', 'scheduled'].includes(row.lifecycle);
  }
  async grant(): Promise<void> {
    if (!this.valid() || !this.canGrant || !this.catalog) { return; }
    const scope = Object.freeze({type: 'company' as const, id: this.companyId});
    const body: ScopedGrant = Object.freeze({expectedCatalogVersion: this.catalog.catalogVersion, bundleId: 'payroll_operations', scope});
    this.attempt = {kind: 'grant', target: this.subjectId, key: this.makeKey(), body, uncertain: false};
    await this.submit();
  }
  async revoke(row: ScopedAssignment): Promise<void> {
    if (!this.valid() || !this.canRevoke(row) || !this.catalog) { return; }
    this.attempt = {kind: 'revoke', target: row.assignmentId, key: this.makeKey(),
      body: Object.freeze({expectedCatalogVersion: this.catalog.catalogVersion}), uncertain: false};
    await this.submit();
  }
  async retry(): Promise<void> { if (this.valid() && this.canRetry) { await this.submit(); } }
  private async submit(): Promise<void> {
    const attempt = this.attempt;
    if (!attempt || this.saving || !this.valid()) { return; }
    this.saving = true; this.needsReload = true; this.success = ''; this.mutationError = '';
    try {
      const result = attempt.kind === 'grant'
        ? await this.api.grant(attempt.target, attempt.body as ScopedGrant, attempt.key, this.valid)
        : await this.api.revoke(attempt.target, attempt.body, attempt.key, this.valid);
      if (!this.valid() || this.attempt !== attempt) { return; }
      if (!this.validMutation(result, attempt)) { throw new Error('Unconfirmed response'); }
      const messages = {granted: 'Assignment recorded.', already_active: 'Assignment was already recorded.',
        revoked: 'Revocation recorded.', already_revoked: 'Assignment was already revoked.', already_inactive: 'Assignment was already inactive.'};
      this.success = messages[result.outcome];
      this.attempt = null; this.saving = false;
      // Replay is an old snapshot; only a new list may update the displayed rows.
      await this.load();
    } catch (error: any) {
      if (!this.valid() || this.attempt !== attempt) { return; }
      this.needsReload = true;
      if (error?.status === 403) { this.deny(error); return; }
      const code = error?.error?.reasonCode;
      const rejected = [400, 404, 409].includes(error?.status) && typeof code === 'string';
      if (rejected && !attempt.uncertain && code !== 'IDEMPOTENCY_KEY_CONFLICT') {
        this.attempt = null;
        if (code === 'CATALOG_VERSION_MISMATCH') { this.catalog = null; }
        this.mutationError = error.status === 400 ? 'Request rejected. Check the selected user, company, and supported bundle.'
          : error.status === 404 ? 'Assignment no longer found. Refresh current state.'
          : 'Request conflicts with current state or catalog. Refresh and review before starting a new attempt.';
      } else {
        attempt.uncertain = true;
        this.mutationError = 'Operation could not be confirmed. It may have been recorded. Retry the same attempt to resolve it; details are locked.';
      }
    } finally { if (this.alive) { this.saving = false; } }
  }
  private validMutation(result: ScopedMutation, attempt: Attempt): boolean {
    const a = result?.assignment;
    return result?.enforcementStatus === 'dormant' && typeof result.revision === 'string' && /^\d+$/.test(result.revision)
      && !!a && a.subject.type === 'human_user' && a.subject.id === this.subjectId && a.bundleId === 'payroll_operations'
      && a.scope.type === 'company' && (attempt.kind === 'grant'
        ? ['granted', 'already_active'].includes(result.outcome) && a.scope.id === (attempt.body as ScopedGrant).scope.id
        : ['revoked', 'already_revoked', 'already_inactive'].includes(result.outcome) && a.assignmentId === attempt.target);
  }
  private deny(error: any): void {
    if (error?.status === 403) {
      this.denied = true; this.page = null; this.attempt = null; ++this.loadVersion; this.loading = false;
      this.mutationError = 'Access denied by the server. Close this dialog. Your legacy roles have not been changed.';
    }
  }
}
